/**
 * Utility functions for analyzing WhatsApp Business template parameters
 */

/**
 * Extracts parameter count from text containing {{1}}, {{2}}, etc.
 * @param {string} text - The text to analyze
 * @returns {number} - The number of unique parameters found
 */
export const extractParameterCount = (text) => {
    if (!text) return 0;
    const matches = text.match(/\{\{(\d+)\}\}/g) || [];
    if (matches.length === 0) return 0;
    
    // Extract unique parameter numbers
    const paramNumbers = matches.map((match) =>
        parseInt(match.replace(/\{\{|\}\}/g, ""))
    );
    
    // Return the maximum parameter number found (not just count)
    // This handles cases where parameters might not be sequential ({{1}}, {{3}}, {{5}})
    return Math.max(...paramNumbers);
};

/**
 * Analyzes template components and returns parameter information
 * @param {Object} templateDetails - Template details object with components array
 * @returns {Object} - Object containing parameter information for each component type
 * 
 * Returns structure:
 * {
 *   header: { hasParameters: boolean, type: string, parameterType: string, count: number },
 *   body: { hasParameters: boolean, type: string, parameterType: string, count: number },
 *   buttons: { hasParameters: boolean, type: string, parameterType: string, count: number, buttons: Array }
 * }
 */
export const analyzeTemplateParameters = (templateDetails) => {
    const result = {
        header: {
            hasParameters: false,
            type: null,
            parameterType: null,
            count: 0,
        },
        body: {
            hasParameters: false,
            type: null,
            parameterType: null,
            count: 0,
        },
        buttons: {
            hasParameters: false,
            type: null,
            parameterType: null,
            count: 0,
            buttons: [],
        },
    };

    if (!templateDetails || !templateDetails.components) {
        return result;
    }

    // Analyze Header Component
    const headerComponent = templateDetails.components.find(
        (c) => c.type === "HEADER"
    );

    if (headerComponent) {
        result.header.type = "HEADER";
        
        if (headerComponent.format === "TEXT" && headerComponent.text) {
            // Text header can have text parameters
            const paramCount = extractParameterCount(headerComponent.text);
            result.header.hasParameters = paramCount > 0;
            result.header.parameterType = "TEXT";
            result.header.count = paramCount;
        } else if (["IMAGE", "VIDEO", "DOCUMENT"].includes(headerComponent.format)) {
            // Media headers don't have text parameters, they use media handles
            result.header.hasParameters = false;
            result.header.parameterType = headerComponent.format; // "IMAGE", "VIDEO", or "DOCUMENT"
            result.header.count = 0;
        } else {
            result.header.parameterType = headerComponent.format || "TEXT";
        }
    }

    // Analyze Body Component
    const bodyComponent = templateDetails.components.find(
        (c) => c.type === "BODY"
    );

    if (bodyComponent && bodyComponent.text) {
        result.body.type = "BODY";
        const paramCount = extractParameterCount(bodyComponent.text);
        result.body.hasParameters = paramCount > 0;
        result.body.parameterType = "TEXT";
        result.body.count = paramCount;
    }

    // Analyze Buttons Component
    const buttonsComponent = templateDetails.components.find(
        (c) => c.type === "BUTTONS"
    );

    if (buttonsComponent && buttonsComponent.buttons) {
        result.buttons.type = "BUTTONS";
        result.buttons.buttons = buttonsComponent.buttons;
        
        // Check if any button has URL with parameters
        let totalButtonParams = 0;
        buttonsComponent.buttons.forEach((button) => {
            if (button.type === "URL" && button.url) {
                // URL buttons can have parameters in the URL (e.g., {{1}})
                const urlParamCount = extractParameterCount(button.url);
                if (urlParamCount > 0) {
                    totalButtonParams += urlParamCount;
                    result.buttons.hasParameters = true;
                }
            }
            // PHONE_NUMBER and QUICK_REPLY buttons don't typically have parameters
        });
        
        result.buttons.count = totalButtonParams;
        result.buttons.parameterType = totalButtonParams > 0 ? "URL" : "NONE";
    }

    return result;
};

/**
 * Gets parameter information for a specific component type
 * @param {Object} templateDetails - Template details object with components array
 * @param {string} componentType - Component type to analyze ("HEADER", "BODY", "BUTTONS")
 * @returns {Object|null} - Parameter information for the component or null if not found
 */
export const getComponentParameterInfo = (templateDetails, componentType) => {
    const analysis = analyzeTemplateParameters(templateDetails);
    
    switch (componentType.toUpperCase()) {
        case "HEADER":
            return analysis.header;
        case "BODY":
            return analysis.body;
        case "BUTTONS":
            return analysis.buttons;
        default:
            return null;
    }
};

/**
 * Checks if template has any parameters
 * @param {Object} templateDetails - Template details object with components array
 * @returns {boolean} - True if template has any parameters, false otherwise
 */
export const hasAnyParameters = (templateDetails) => {
    const analysis = analyzeTemplateParameters(templateDetails);
    return (
        analysis.header.hasParameters ||
        analysis.body.hasParameters ||
        analysis.buttons.hasParameters
    );
};

/**
 * Gets total parameter count across all components
 * @param {Object} templateDetails - Template details object with components array
 * @returns {number} - Total number of parameters across all components
 */
export const getTotalParameterCount = (templateDetails) => {
    const analysis = analyzeTemplateParameters(templateDetails);
    return (
        analysis.header.count +
        analysis.body.count +
        analysis.buttons.count
    );
};

